/**
 * Three Step Solution — Phone Pass Play state machine.
 *
 * Local-first: no Supabase writes. AI seats fill instantly from the
 * persona templates (`aiPickActions`, `aiWriteSteps`) so the pass flow
 * never waits on the network. When the device is online, the App layer
 * asks Claude Haiku (`passplay-ai` edge function) for fresher lines and
 * swaps them in before they're revealed — both writers here SKIP an AI
 * seat that already has content, so a Haiku prefill is never clobbered
 * by a template. Offline, templates play as-is.
 *
 * Phase flow (per round):
 *   intro → write_actions × N → form_prompt → write_steps × N →
 *   reveal × N → vote × N → between_rounds → next round | results
 *
 * Pass-the-phone splash screens slot between every per-player phase
 * so nobody peeks at someone else's input.
 */

import { aiPickActions, aiWriteSteps } from '../ai/personas'
import type { PassPlayPlayer } from '../../passplay/types'

const ROUNDS = 3

export type ThreeStepPassPlayPhase =
  | 'intro'                    // explain rules, tap to start
  | 'pass_to_actions'          // splash before currentPlayer writes actions
  | 'write_actions'            // currentPlayer writes 3 actions
  | 'phase1_complete'          // all actions in, tap to reveal round prompt
  | 'round_intro'              // "Round N — How do you X while Y"
  | 'pass_to_steps'            // splash before currentPlayer writes steps
  | 'write_steps'              // currentPlayer writes 3 steps
  | 'reveal'                   // everyone watches, tap through reveals
  | 'pass_to_vote'             // splash before currentPlayer votes
  | 'vote'                     // currentPlayer votes
  | 'between_rounds'           // standings, tap for next round
  | 'results'                  // final scoreboard

export interface PlayerActions {
  /** Three action phrases for phase 1. */
  actions: [string, string, string]
}

export interface PlayerSteps {
  /** Three steps answering the round's prompt. */
  steps: [string, string, string]
}

export interface RoundData {
  actionA: string
  actionB: string
  actionAOwner: string
  actionBOwner: string
  /** playerId → their three steps for THIS round. */
  steps: Record<string, PlayerSteps>
  /** voterId → playerId they voted for. */
  votes: Record<string, string>
}

export interface ThreeStepPassPlayState {
  players: PassPlayPlayer[]
  phase: ThreeStepPassPlayPhase
  /** Whose turn it currently is (index into players). Unused in shared
   *  phases (round_intro, reveal, between_rounds, results). */
  currentPlayerIdx: number
  /** Phase 1 actions submitted by each player. */
  playerActions: Record<string, PlayerActions>
  /** Random pool: action ids (player_id + slot). Used to pick the round
   *  prompt without ever picking the same action twice across rounds. */
  unusedActionRefs: { playerId: string; slot: 0 | 1 | 2 }[]
  rounds: RoundData[]
  currentRound: number          // 0-indexed
  /** Per-player local reveal index for the current round. Increments
   *  as each player taps Next. The reveal phase ends per-device when
   *  the player has seen all N solutions. */
  revealIndex: number           // shared across players for simplicity
  /** Running total votes earned across all rounds. */
  totalVotes: Record<string, number>
}

/** Build a fresh state — players seated, ready for the intro. */
export function createState(players: PassPlayPlayer[]): ThreeStepPassPlayState {
  return {
    players,
    phase: 'intro',
    currentPlayerIdx: 0,
    playerActions: {},
    unusedActionRefs: [],
    rounds: [],
    currentRound: 0,
    revealIndex: 0,
    totalVotes: Object.fromEntries(players.map(p => [p.id, 0])),
  }
}

// ───── Phase transitions ─────────────────────────────────────────────

/** Tap to begin → moves us into the first per-player actions write. */
export function startPhase1(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return advanceToActionWriter(s, 0)
}

/** Move to the next player who still needs to write actions, with the
 *  pass-the-phone splash in between. AI seats auto-fill and skip ahead. */
function advanceToActionWriter(
  s: ThreeStepPassPlayState, startIdx: number,
): ThreeStepPassPlayState {
  let st = s
  let idx = startIdx
  while (idx < st.players.length) {
    const p = st.players[idx]
    if (p.kind === 'ai') {
      // AI submits inline. We don't pause for them. Skip seats that
      // already have actions (e.g. a Haiku prefill from the App layer).
      if (!st.playerActions[p.id]) {
        const [a, b, c] = aiPickActions(p.name)
        st = {
          ...st,
          playerActions: { ...st.playerActions, [p.id]: { actions: [a, b, c] } },
        }
      }
      idx++
      continue
    }
    return { ...st, phase: 'pass_to_actions', currentPlayerIdx: idx }
  }
  return enterPhase1Complete(st)
}

/** "I'm ready" tap on the pass-to-actions splash → write screen. */
export function readyToWriteActions(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return { ...s, phase: 'write_actions' }
}

/** Submit the human's three actions. */
export function submitActions(
  s: ThreeStepPassPlayState, actions: [string, string, string],
): ThreeStepPassPlayState {
  const cur = s.players[s.currentPlayerIdx]
  if (!cur) return s
  const next: ThreeStepPassPlayState = {
    ...s,
    playerActions: { ...s.playerActions, [cur.id]: { actions } },
  }
  return advanceToActionWriter(next, s.currentPlayerIdx + 1)
}

function enterPhase1Complete(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  // Build the unused refs pool from everyone's submitted actions.
  const refs: ThreeStepPassPlayState['unusedActionRefs'] = []
  for (const p of s.players) {
    const a = s.playerActions[p.id]
    if (!a) continue
    for (let slot = 0; slot < 3; slot++) {
      const text = a.actions[slot].trim()
      if (text.length > 0) refs.push({ playerId: p.id, slot: slot as 0 | 1 | 2 })
    }
  }
  return { ...s, phase: 'phase1_complete', unusedActionRefs: refs }
}

/** Tap to begin round 1 → picks the first prompt + advances to writing steps. */
export function beginRound(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  if (s.unusedActionRefs.length < 2) {
    return { ...s, phase: 'results' }
  }
  // Pick two refs from different players if possible.
  const pool = s.unusedActionRefs.slice()
  const aIdx = Math.floor(Math.random() * pool.length)
  const aRef = pool[aIdx]
  pool.splice(aIdx, 1)

  const otherOwners = pool.filter(r => r.playerId !== aRef.playerId)
  const fallback = pool
  const sourcePool = otherOwners.length > 0 ? otherOwners : fallback
  const bRef = sourcePool[Math.floor(Math.random() * sourcePool.length)]
  // Remove bRef from main pool too.
  const remainingRefs = s.unusedActionRefs.filter(
    r => !(r.playerId === aRef.playerId && r.slot === aRef.slot)
       && !(r.playerId === bRef.playerId && r.slot === bRef.slot),
  )

  const actionA = s.playerActions[aRef.playerId].actions[aRef.slot]
  const actionB = s.playerActions[bRef.playerId].actions[bRef.slot]

  const newRound: RoundData = {
    actionA, actionB,
    actionAOwner: aRef.playerId, actionBOwner: bRef.playerId,
    steps: {}, votes: {},
  }
  return {
    ...s,
    phase: 'round_intro',
    unusedActionRefs: remainingRefs,
    rounds: [...s.rounds, newRound],
    currentRound: s.rounds.length,
    revealIndex: 0,
  }
}

/** Tap "Got it, let's write" on the round intro splash. */
export function startWritingSteps(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return advanceToStepsWriter(s, 0)
}

function advanceToStepsWriter(
  s: ThreeStepPassPlayState, startIdx: number,
): ThreeStepPassPlayState {
  const round = s.rounds[s.currentRound]
  if (!round) return s
  let st = s
  let idx = startIdx
  while (idx < st.players.length) {
    const p = st.players[idx]
    if (p.kind === 'ai') {
      // Skip seats that already have steps for this round (Haiku
      // prefill from the App layer beats the template).
      if (!st.rounds[st.currentRound]?.steps[p.id]) {
        const [s1, s2, s3] = aiWriteSteps(p.name, round.actionA, round.actionB)
        st = {
          ...st,
          rounds: st.rounds.map((r, i) =>
            i === st.currentRound
              ? { ...r, steps: { ...r.steps, [p.id]: { steps: [s1, s2, s3] } } }
              : r),
        }
      }
      idx++
      continue
    }
    return { ...st, phase: 'pass_to_steps', currentPlayerIdx: idx }
  }
  return { ...st, phase: 'reveal', revealIndex: 0 }
}

export function readyToWriteSteps(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return { ...s, phase: 'write_steps' }
}

export function submitSteps(
  s: ThreeStepPassPlayState, steps: [string, string, string],
): ThreeStepPassPlayState {
  const cur = s.players[s.currentPlayerIdx]
  if (!cur) return s
  const next: ThreeStepPassPlayState = {
    ...s,
    rounds: s.rounds.map((r, i) =>
      i === s.currentRound
        ? { ...r, steps: { ...r.steps, [cur.id]: { steps } } }
        : r),
  }
  return advanceToStepsWriter(next, s.currentPlayerIdx + 1)
}

/** Reveal: tap "Next" cycles through each player's solution in
 *  join_order. When done, transitions to first vote handoff. */
export function nextReveal(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  const total = s.players.length
  if (s.revealIndex + 1 < total) {
    return { ...s, revealIndex: s.revealIndex + 1 }
  }
  return advanceToVoter(s, 0)
}

function advanceToVoter(
  s: ThreeStepPassPlayState, startIdx: number,
): ThreeStepPassPlayState {
  const round = s.rounds[s.currentRound]
  if (!round) return s
  let st = s
  let idx = startIdx
  while (idx < st.players.length) {
    const p = st.players[idx]
    if (p.kind === 'ai') {
      // AI votes for a random other player.
      const others = st.players.filter(o => o.id !== p.id)
      const target = others[Math.floor(Math.random() * others.length)]
      st = {
        ...st,
        rounds: st.rounds.map((r, i) =>
          i === st.currentRound
            ? { ...r, votes: { ...r.votes, [p.id]: target.id } }
            : r),
      }
      idx++
      continue
    }
    return { ...st, phase: 'pass_to_vote', currentPlayerIdx: idx }
  }
  return tallyAndAdvance(st)
}

export function readyToVote(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return { ...s, phase: 'vote' }
}

export function castVote(
  s: ThreeStepPassPlayState, targetPlayerId: string,
): ThreeStepPassPlayState {
  const cur = s.players[s.currentPlayerIdx]
  if (!cur) return s
  const next: ThreeStepPassPlayState = {
    ...s,
    rounds: s.rounds.map((r, i) =>
      i === s.currentRound
        ? { ...r, votes: { ...r.votes, [cur.id]: targetPlayerId } }
        : r),
  }
  return advanceToVoter(next, s.currentPlayerIdx + 1)
}

function tallyAndAdvance(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  const round = s.rounds[s.currentRound]
  if (!round) return s
  // Tally this round's votes into running totals.
  const totals = { ...s.totalVotes }
  for (const target of Object.values(round.votes)) {
    totals[target] = (totals[target] ?? 0) + 1
  }
  if (s.currentRound + 1 >= ROUNDS || s.unusedActionRefs.length < 2) {
    return { ...s, phase: 'results', totalVotes: totals }
  }
  return { ...s, phase: 'between_rounds', totalVotes: totals }
}

export function nextRound(s: ThreeStepPassPlayState): ThreeStepPassPlayState {
  return beginRound(s)
}
